import { reportError } from "@/utils/reportError";
import { trackNotificationPreferenceChanged } from '@/utils/analytics';
import { useProfile } from "@/contexts/ProfileContext";
import LoadingScreen from "@/components/LoadingScreen";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { registerForPushNotificationsAsync, savePushTokenToDatabase, sendTestNotification } from "@/hooks/usePushNotifications";

import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import * as Notifications from "expo-notifications";
import { Stack, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Alert,
    Linking,
    ScrollView,
    StyleSheet,
    Switch,
    Text,
    TouchableOpacity,
    View
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function NotificationSettingsScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { preferences, loading, updatePreference } = useNotificationPreferences();
  const [updating, setUpdating] = useState(false);
  const [permissionsGranted, setPermissionsGranted] = useState(true);
  // iOS : tant que l'app n'a JAMAIS demandé la permission (undetermined), la
  // section Notifications n'existe même pas dans les Réglages iPhone —
  // envoyer l'utilisateur là-bas est un cul-de-sac. Dans ce cas on demande
  // directement dans l'app ; on ne renvoie vers les Réglages que si c'est
  // réellement refusé (canAskAgain === false).
  const [canAskAgain, setCanAskAgain] = useState(false);
  // Section Debug/Actions (réenregistrer le token, notification de test) :
  // outillage interne, réservé aux comptes admin QA (profiles.is_admin).
  const { isAdmin } = useProfile();

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const { status, canAskAgain: askable } = await Notifications.getPermissionsAsync();
    setPermissionsGranted(status === 'granted');
    setCanAskAgain(status !== 'granted' && askable);
  };

  // Demande la permission DANS l'app (prompt système) puis enregistre le
  // token — même mécanique que l'écran d'onboarding notification-permission.
  const requestPermissionInApp = async () => {
    try {
      setUpdating(true);
      const token = await registerForPushNotificationsAsync();
      if (token) await savePushTokenToDatabase(token);
    } catch (err) {
      reportError(err);
    } finally {
      setUpdating(false);
      checkPermissions();
    }
  };

  const handleToggle = async (
    key: keyof typeof preferences,
    value: boolean
  ) => {
    setUpdating(true);
    const result = await updatePreference(key, value);
    setUpdating(false);

    if (result.success) {
      trackNotificationPreferenceChanged(key, value);
    }

    if (!result.success) {
      Alert.alert(t("common.errorTitle"), t("notifications.updateError"));
    }
  };

  if (loading) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <LoadingScreen />
      </>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <Stack.Screen options={{ headerShown: false }} />
      
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 1]}
        style={styles.gradientContainer}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity
              onPress={() => router.back()}
              style={styles.backButton}
            >
              <Ionicons name="arrow-back" size={24} color="#000" />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>{t("notifications.headerTitle")}</Text>
            <View style={{ width: 24 }} />
          </View>

          {/* Info Card */}
          <View style={styles.infoCard}>
            <Ionicons name="notifications" size={24} color="#CD7728" />
            <Text style={styles.infoText}>
              {t("notifications.infoText")}
            </Text>
          </View>

          {/* Types de Notifications */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("notifications.typesTitle")}</Text>

            {!permissionsGranted && (
              <View style={styles.permissionWarning}>
                <Ionicons name="warning" size={20} color="#FF9500" />
                <View style={styles.permissionWarningContent}>
                  <Text style={styles.permissionWarningTitle}>
                    {t("notifications.permissionWarningTitle")}
                  </Text>
                  <Text style={styles.permissionWarningText}>
                    {t("notifications.permissionWarningText")}
                  </Text>
                  <TouchableOpacity
                    style={styles.openSettingsButton}
                    onPress={canAskAgain ? requestPermissionInApp : () => Linking.openSettings()}
                    disabled={updating}
                  >
                    <Text style={styles.openSettingsText}>
                      {canAskAgain
                        ? t("notifications.enableNow")
                        : t("notifications.openSettings")}
                    </Text>
                    <Ionicons name="arrow-forward" size={16} color="#CD7728" />
                  </TouchableOpacity>
                </View>
              </View>
            )}

            <View style={styles.settingItem}>
              <View style={styles.settingLeft}>
                <Ionicons name="help-circle" size={20} color="#CD7728" />
                <View style={styles.settingTextContainer}>
                  <Text style={styles.settingTitle}>{t("notifications.quizTitle")}</Text>
                  <Text style={styles.settingSubtext}>
                    {t("notifications.quizSubtext")}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.quizEnabled}
                onValueChange={(value) => handleToggle("quizEnabled", value)}
                disabled={updating}
                trackColor={{ false: "#D1D5DB", true: "#83BF4F" }}
                thumbColor="#FFF"
              />
            </View>

            <View style={styles.settingItem}>
              <View style={styles.settingLeft}>
                <Ionicons name="flag" size={20} color="#CD7728" />
                <View style={styles.settingTextContainer}>
                  <Text style={styles.settingTitle}>{t("notifications.objectivesTitle")}</Text>
                  <Text style={styles.settingSubtext}>
                    {t("notifications.objectivesSubtext")}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.objectivesEnabled}
                onValueChange={(value) =>
                  handleToggle("objectivesEnabled", value)
                }
                disabled={updating}
                trackColor={{ false: "#D1D5DB", true: "#83BF4F" }}
                thumbColor="#FFF"
              />
            </View>

            <View style={styles.settingItem}>
              <View style={styles.settingLeft}>
                <Ionicons name="gift" size={20} color="#CD7728" />
                <View style={styles.settingTextContainer}>
                  <Text style={styles.settingTitle}>{t("notifications.wheelTitle")}</Text>
                  <Text style={styles.settingSubtext}>
                    {t("notifications.wheelSubtext")}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.wheelEnabled}
                onValueChange={(value) => handleToggle("wheelEnabled", value)}
                disabled={updating}
                trackColor={{ false: "#D1D5DB", true: "#83BF4F" }}
                thumbColor="#FFF"
              />
            </View>
          </View>



          {/* Info supplémentaire */}
          <View style={styles.footerInfo}>
            <Ionicons name="information-circle-outline" size={16} color="#666" />
            <Text style={styles.footerText}>
              {t("notifications.footerText")}
            </Text>
          </View>

          {/* Debug/Actions Section — outillage QA, admins uniquement */}
          {isAdmin && (
          <View style={[styles.section, { marginTop: 24 }]}>
            <Text style={styles.sectionTitle}>{t("notifications.actionsTitle")}</Text>
            
            <TouchableOpacity
              style={styles.actionButton}
              onPress={async () => {
                try {
                  setUpdating(true);
                  const token = await registerForPushNotificationsAsync();
                  if (token) {
                    const result = await savePushTokenToDatabase(token);
                    if (result?.success) {
                      Alert.alert(t("notifications.tokenSavedTitle"), t("notifications.tokenSavedMessage"));
                    } else {
                      Alert.alert(t("common.errorTitle"), result?.error || t("notifications.tokenSaveError"));
                    }
                  } else {
                    Alert.alert(t("common.errorTitle"), t("notifications.tokenPermissionError"));
                  }
                } catch (error: any) {
                  reportError(error);
                  Alert.alert(t("common.errorTitle"), t("notifications.tokenSaveError"));
                } finally {
                  setUpdating(false);
                }
              }}
              disabled={updating}
            >
              <Ionicons name="refresh" size={20} color="#CD7728" />
              <Text style={styles.actionButtonText}>{t("notifications.reregisterToken")}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={async () => {
                try {
                  await sendTestNotification();
                  Alert.alert(t("notifications.testSentTitle"), t("notifications.testSentMessage"));
                } catch (error: any) {
                  reportError(error);
                  Alert.alert(t("common.errorTitle"), t("notifications.testError"));
                }
              }}
              disabled={updating}
            >
              <Ionicons name="notifications-outline" size={20} color="#CD7728" />
              <Text style={styles.actionButtonText}>{t("notifications.sendTest")}</Text>
            </TouchableOpacity>
          </View>
          )}
        </ScrollView>
      </LinearGradient>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FBEFE3",
  },
  gradientContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    marginTop: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
  },
  infoCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  infoText: {
    flex: 1,
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    marginLeft: 12,
    lineHeight: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#000",
    marginBottom: 12,
  },
  settingItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  settingLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  settingTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  settingTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 15,
    color: "#000",
  },
  settingSubtext: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#999",
    marginTop: 2,
  },
  permissionWarning: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#FFF9F0",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#FFE5CC",
  },
  permissionWarningContent: {
    flex: 1,
    marginLeft: 12,
  },
  permissionWarningTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 15,
    color: "#FF9500",
    marginBottom: 4,
  },
  permissionWarningText: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#666",
    lineHeight: 18,
    marginBottom: 12,
  },
  openSettingsButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  openSettingsText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#CD7728",
  },
  footerInfo: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 16,
  },
  footerText: {
    flex: 1,
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#666",
    lineHeight: 18,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    gap: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  actionButtonText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 15,
    color: "#CD7728",
  },
});


